/**
 * relay-server's HTTP surface, as functions.
 *
 * One function per route, named for the route, returning the server's own shape
 * — these are wire types, and `store.ts` is where they become the view types in
 * `types.ts`. Two layers rather than one because the two change for different
 * reasons: relay-server owns what its API says, and a screen that read the wire
 * directly would have to change when it moved.
 *
 * Every path is relative. The Worker proxies `/api/*` to relay-server over a
 * service binding, so the page, the API and the socket share one origin and the
 * `mauvely_session` cookie rides along on its own — no CORS, no preflight, no
 * token to hold. `lib/api.ts` carries that argument in full.
 *
 * ── The guest's credential is a header, and it is `lib/api.ts`'s ────────────
 * A guest has no account and no cookie: `POST /api/relay/meetings/join` hands
 * back a `meetingToken` which every later request presents as
 * `x-relay-meeting-token`. That used to be a wrapper around `api` in this file,
 * which covered the routes here and none of the ones that call `api` directly —
 * the SFU signalling, the roster poll and the recording upload among them. It
 * now lives in `api` itself, so `relay` is `api` and the two names are kept
 * apart only by what they mean.
 */

import { api, setMeetingToken } from '../api';

export { currentMeetingToken, setMeetingToken } from '../api';

const relay = api;

const body = (value: unknown): RequestInit => ({ body: JSON.stringify(value) });

// ── people ──────────────────────────────────────────────────────────────────

/** The directory row, and `me`'s, which is the same shape. */
export interface WireUser {
  id: string;
  email: string;
  name: string;
  handle: string | null;
  avatarUrl: string | null;
  jobTitle: string;
  organisation: string;
  bio: string;
  self: boolean;
}

export const getMe = (): Promise<{ user: WireUser }> => relay('/api/relay/me');

export const getUser = (id: string): Promise<{ user: WireUser }> =>
  relay(`/api/relay/users/${encodeURIComponent(id)}`);

export const getUserByEmail = (email: string): Promise<{ user: WireUser }> =>
  relay(`/api/relay/users/by-email/${encodeURIComponent(email)}`);

export const getUserByHandle = (handle: string): Promise<{ user: WireUser }> =>
  relay(`/api/relay/users/by-handle/${encodeURIComponent(handle)}`);

// ── conversations ───────────────────────────────────────────────────────────

/** The directory row. No `head`, no `epoch` and no members: those live in the
 *  Durable Object, and fetching them here would be one subrequest per
 *  conversation on the launch path. `sync` answers the first two in one round
 *  trip; `getConversation` answers the third. */
export interface WireConversationRow {
  id: string;
  kind: string;
  title: string;
  topic: string;
  createdBy: string;
  createdAt: number;
  joinedAt: number;
  role: string;
  hasFiles: boolean;
  /** Present, and true, only on the account's official Mauvely conversation. */
  official?: boolean;
}

export interface WireConversation {
  id: string;
  kind: string;
  title: string;
  topic: string;
  head: number;
  epoch: number;
  availableFrom: number;
  members: string[];
  owner: string | null;
  files: { folderId: string; linkId: string; secret: string } | null;
  official?: boolean;
}

export const listConversations = (): Promise<{ conversations: WireConversationRow[] }> =>
  relay('/api/relay/conversations');

export const getConversation = (id: string): Promise<WireConversation> =>
  relay(`/api/relay/conversations/${encodeURIComponent(id)}`);

export const createConversation = (input: {
  kind: 'direct' | 'group' | 'channel';
  title?: string;
  members: string[];
}): Promise<{ id: string; kind: string; title: string; members: string[] }> =>
  relay('/api/relay/conversations', { method: 'POST', ...body(input) });

/**
 * Rename or re-topic. Owner only, and never a direct conversation — that one is
 * titled by its people and the route says so with a 400.
 *
 * The answer is **not** a conversation: it is the two fields that changed and
 * the folder binding, with no id and no members. A caller that fed it to
 * whatever adopts a `GET` would write a row with an undefined id.
 */
export interface WireConversationPatch {
  ok: boolean;
  title: string;
  topic: string;
  files: { folderId: string; linkId: string; secret: string } | null;
}

export const patchConversation = (
  id: string,
  input: { title?: string; topic?: string }
): Promise<WireConversationPatch> =>
  relay(`/api/relay/conversations/${encodeURIComponent(id)}`, { method: 'PATCH', ...body(input) });

export const addMember = (id: string, userId: string): Promise<{ ok: boolean }> =>
  relay(`/api/relay/conversations/${encodeURIComponent(id)}/members`, {
    method: 'POST',
    ...body({ userId })
  });

/** Leaving is removing yourself, which is the same route. */
export const removeMember = (id: string, userId: string): Promise<{ ok: boolean }> =>
  relay(
    `/api/relay/conversations/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
    { method: 'DELETE' }
  );

/** The owner's delete-for-everyone. Every member gets a `membership` alert on
 *  their socket, and the conversation is gone from `GET /conversations` and
 *  404s from `GET /conversations/:id` for all of them afterwards. */
export const deleteConversation = (id: string): Promise<{ ok: boolean }> =>
  relay(`/api/relay/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' });

// ── meetings ────────────────────────────────────────────────────────────────

/**
 * The meeting row.
 *
 * `conversationId`, `policy` and `recording` are Part 2 of
 * `relay-server/docs/calls-and-meetings.md` and arrive only where migration
 * 0009 has been applied — so they are optional here and defaulted in the store.
 * A client that demanded them would fail against a server that is simply
 * behind, which is the state relay-server is in on `main` today.
 */
export interface WireMeeting {
  id: string;
  ownerId: string;
  title: string;
  scheduledAt: number | null;
  status: string;
  startedAt: number | null;
  endedAt: number | null;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
  /** Bare account ids. It stayed a plain array when roles arrived, because it
   *  is what every shipped client reads. */
  invitees?: unknown;
  /** The second tier, beside it. Everybody else in `invitees` is an attendee. */
  cohosts?: unknown;
  conversationId?: string;
  policy?: unknown;
  recording?: boolean;
  /** The one time the join token is returned. Every later read answers without
   *  it; a host who lost it rotates rather than reads. */
  joinToken?: string;
}

export interface WireLobbyParty {
  id: string;
  kind: 'user' | 'guest';
  displayName: string;
  at: number;
}

export const listMeetings = (since?: number): Promise<{ meetings: WireMeeting[] }> =>
  relay(since === undefined ? '/api/relay/meetings' : `/api/relay/meetings?since=${since}`);

export const createMeeting = (input: {
  title?: string;
  scheduledAt?: number;
  conversationId?: string;
  invitees?: unknown;
  policy?: unknown;
}): Promise<WireMeeting> => relay('/api/relay/meetings', { method: 'POST', ...body(input) });

export const getMeeting = (id: string): Promise<WireMeeting> =>
  relay(`/api/relay/meetings/${encodeURIComponent(id)}`);

export const patchMeeting = (
  id: string,
  input: { title?: string; scheduledAt?: number; policy?: unknown; invitees?: unknown }
): Promise<WireMeeting> =>
  relay(`/api/relay/meetings/${encodeURIComponent(id)}`, { method: 'PATCH', ...body(input) });

export const endMeeting = (id: string): Promise<{ ok: boolean }> =>
  relay(`/api/relay/meetings/${encodeURIComponent(id)}/end`, { method: 'POST', ...body({}) });

export const cancelMeeting = (id: string): Promise<{ ok: boolean }> =>
  relay(`/api/relay/meetings/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** The old link stops working. That is what rotation is for, and it is also the
 *  only way to read a token the client did not keep from the create. */
export const rotateLink = (id: string): Promise<{ meetingId: string; joinToken: string }> =>
  relay(`/api/relay/meetings/${encodeURIComponent(id)}/link`, { method: 'POST', ...body({}) });

export const getLobby = (id: string): Promise<{ waiting: WireLobbyParty[] }> =>
  relay(`/api/relay/meetings/${encodeURIComponent(id)}/lobby`);

export const admitParty = (id: string, partyId: string): Promise<{ ok: boolean; state: string }> =>
  relay(
    `/api/relay/meetings/${encodeURIComponent(id)}/lobby/${encodeURIComponent(partyId)}`,
    { method: 'POST', ...body({}) }
  );

export const denyParty = (id: string, partyId: string): Promise<{ ok: boolean; state: string }> =>
  relay(
    `/api/relay/meetings/${encodeURIComponent(id)}/lobby/${encodeURIComponent(partyId)}`,
    { method: 'DELETE' }
  );

export interface WireStanding {
  meetingId: string;
  title: string;
  status: string;
  participantId: string;
  displayName: string;
  guest: boolean;
  state: 'none' | 'knocking' | 'admitted' | 'denied';
}

/** Where this party stands. Reading a standing never creates one — `none` is
 *  somebody who has never presented themselves, and the route answers 404 for
 *  it rather than seating them. */
export const myStanding = (id: string): Promise<WireStanding> =>
  relay(`/api/relay/meetings/${encodeURIComponent(id)}/me`);

export interface WireJoinAnswer extends WireStanding {
  meetingToken?: string;
  expiresAt?: number;
}

/**
 * The link, redeemed.
 *
 * Two outcomes, decided by whether the caller already has an account: somebody
 * signed in knocks under their own name and keeps their ordinary session, and
 * no guest credential is minted at all. Everybody else gets a `meetingToken`,
 * which this function puts where every later call will find it.
 */
export async function joinMeeting(token: string, displayName?: string): Promise<WireJoinAnswer> {
  const answer = await api<WireJoinAnswer>('/api/relay/meetings/join', {
    method: 'POST',
    ...body(displayName === undefined ? { token } : { token, displayName })
  });
  if (answer.meetingToken) setMeetingToken(answer.meetingToken);
  return answer;
}

// ── translation ──────────────────────────────────────────────────────────────

/** The account's choices, and whether this deployment can translate at all.
 * `language` is a DeepL target code; null means "the language the interface is
 * already in". */
export interface WireTranslationState {
  mode: 'auto' | 'demand';
  language: string | null;
  available: boolean;
}

/** One translated text. `language` is what DeepL detected in the source and
 * `languageName` the same language as a person reads it, resolved by the
 * server so a row never waits on the picker's list. */
export interface WireTranslation {
  text: string;
  language: string;
  languageName: string;
}

/** Up to sixty-four texts in one request — the server's own batch cap, so a
 * transcript page costs one request, not one per message. */
export const translateTexts = (texts: string[], target: string): Promise<{ translations: WireTranslation[] }> =>
  relay('/api/relay/translate', { method: 'POST', ...body({ texts, target }) });

/** What a client may pick as a target, in DeepL's own order. */
export const translateLanguages = (): Promise<{ languages: Array<{ code: string; name: string }> }> =>
  relay('/api/relay/translate/languages');

/** The account's choices. Absent fields on a PATCH mean "unchanged";
 * `language: null` is the explicit choice to follow the interface's language. */
export const getTranslation = (): Promise<WireTranslationState> =>
  relay('/api/relay/translation');

export const patchTranslation = (input: {
  mode?: 'auto' | 'demand';
  language?: string | null;
}): Promise<WireTranslationState> =>
  relay('/api/relay/translation', { method: 'PATCH', ...body(input) });
